import { numFormmater } from "@/pages/Requirement/util";
import { BoxStatus, PackBox } from "@/types/packing";
import { Button, Checkbox, Flex, Form, Input, InputNumber, Modal, notification, Spin, TableProps, Typography } from "antd";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import usePackOrderDetail from "../hooks";
import { PackOrderContext } from "../context";
import { plus } from "@/utils/calc";
import STableLayout from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { AnyObject } from "antd/es/_util/type";
import ExportProgressModal from "@/components/ExportProgressModal";

interface BatchSealProps {
  open: boolean;
  boxes: PackBox[];
  onClose: () => void;
};

const BatchSeal: FC<BatchSealProps> = ({ open, boxes, onClose }) => {
  const { t } = useTranslation();
  const { setOrderData } = useContext(PackOrderContext);
  const { doBox, editBox } = usePackOrderDetail();
  const [items, setItems] = useState<PackBox[]>([]);
  const [batchUse, setBatchUse] = useState<boolean>(false);
  const [sealProgress, setSealProgress] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [filterd, setFilterd] = useState<boolean>(false);


  useEffect(() => {
    if (open && !!boxes.length) {
      let resItems: PackBox[] = [];
      boxes.forEach(box => {
        let netWeight: string = '0';
        box.items.forEach(item => netWeight = plus(netWeight, String(item.netWeight || 0)));
        resItems.push({
          ...box,
          netWeightG: Number(netWeight)
        });
      });
      setItems([...resItems]);
    }
  }, [open, boxes]);

  // 箱号规则（见 utils.ts generateSequence）：字母前缀+数字 或 纯数字，数字部分补零连续递增。
  // 区间筛选按「前缀一致 且 数字值落在 [startNum, endNum]」判断。
  const filterdItems = useMemo(() => {
    if (!filterd) return [...items];
    const parse = (no: string): { prefix: string, num: number } | null => {
      const m = no?.match(/^([A-Za-z]*)(\d+)$/);
      return m ? { prefix: m[1], num: parseInt(m[2], 10) } : null;
    };
    const startInfo = parse(start.trim());
    const endInfo = parse(end.trim());
    if (!startInfo || !endInfo || startInfo.num > endInfo.num) return [...items];
    return items.filter(box => {
      const info = parse(box.boxNumber);
      return !!info && info.prefix === startInfo.prefix
        && info.num >= startInfo.num && info.num <= endInfo.num;
    });
  }, [items, filterd, start, end])

  const handleOk = async () => {
    // let errBox = filterdItems.filter(b => !!!b.grossWeightG)
    // if (!!errBox.length) return notification.error({
    //   message: t('warehouse.gWeightNeed'), description: (
    //     <div style={{ color: "red" }}>{errBox.map(b => b.boxNumber).join(', ')}</div>
    //   )
    // })]
    let validItems = items.filter(box => !!box.grossWeightG);
    if (validItems.length === 0) return notification.error({ message: t('warehouse.gWeightNeed') });
    setSealProgress(0);
    setLoading(true);
    let len = validItems.length;
    try {
      for (let i = 0; i < len; i++) {
        const box = validItems[i];
        let passFlag: boolean = true;
        passFlag = !!(await editBox({ grossWeightG: box.grossWeightG, netWeightG: box.netWeightG }, box.id, true));
        if (!passFlag) {
          notification.error({ message: t('warehouse.batchSealErr'), description: t('warehouse.editBoxErr', { number: box.boxNumber }) });
          return setLoading(false);
        }
        passFlag = !!(await doBox("seal", box.id, true));
        if (!passFlag) {
          notification.error({ message: t('warehouse.batchSealErr'), description: t('warehouse.sealBoxErr', { number: box.boxNumber }) });
          return setLoading(false);
        }
        setSealProgress(Math.round(((i + 1) / len) * 100));
      };
      setSealProgress(100);
      setOrderData(prevOrder => {
        if (prevOrder) return {
          ...prevOrder,
          boxes: (prevOrder.boxes || []).map(box => {
            let newBox = validItems.find(i => i.id === box.id);
            return {
              ...box,
              netWeightG: newBox?.netWeightG || box.netWeightG,
              grossWeightG: newBox?.grossWeightG || box.grossWeightG,
              status: newBox ? BoxStatus.SHUTED : box.status
            }
          })
        }
        else return null;
      })
      setTimeout(() => {
        setLoading(false);
        notification.success({ message: t('warehouse.batchSealSucc') });
        onClose();
      }, 300);
    } catch (error) {
      notification.error({ message: t('warehouse.batchSealErr') })
      setLoading(false);
      setSealProgress(0);
    }
  };

  const handleChangeItem = (val: number, id: string) => {
    let batchIds: string[] = batchUse ? filterdItems.map(i => i.id) : [];
    setItems(last => last.map(row => ({
      ...row,
      grossWeightG: row.id === id ? val : batchIds.includes(row.id) ? val : row.grossWeightG
    })))
  };

  const columns: TableProps<PackBox>['columns'] = [
    {
      title: <SText text={t('warehouse.boxNo')} />,
      width: 120,
      key: "number",
      fixed: "left",
      render: (_, record) => <Typography.Link>{record.boxNumber}</Typography.Link>
    },
    {
      title: <SText text={t('warehouse.netWeightG')} />,
      width: 120,
      key: "netWeightG",
      render: (_, record) => <SText text={numFormmater(record.netWeightG)} />
    },
    {
      title: <SText text={t('warehouse.grossWeightG')} />,
      width: 120,
      key: "grossWeightG",
      render: (_, record) => (
        <InputNumber
          value={record.grossWeightG}
          changeOnWheel
          onChange={val => handleChangeItem(val || 0, record.id)}
          placeholder={t('warehouse.grossWeightG')}
          formatter={val => numFormmater(Number(val || 0))}
          style={{ width: "100%" }}
        />
      )
    },

  ];
  const headerChildren = (
    <Flex style={{ width: "100%" }} vertical>
      <Checkbox checked={batchUse} onChange={e => setBatchUse(e.target.checked)} >{t('warehouse.batchGross')}</Checkbox>
      <Form.Item style={{ marginBottom: 6 }} label={t('warehouse.sealRange')}>
        <Input style={{ width: 120 }} placeholder={t('warehouse.startNo')} value={start} onChange={e => setStart(e.target.value)} /> - <Input style={{ width: 120 }} value={end} placeholder={t('warehouse.endNo')} onChange={e => setEnd(e.target.value)} />
        <Button style={{ margin: "0 4px" }} type="primary" onClick={() => setFilterd(true)}>{t('purchase.search')}</Button>
        <Button onClick={() => setFilterd(false)}>{t('warehouse.reset')}</Button>
      </Form.Item>
    </Flex>
  );

  return (
    <Modal title={t('warehouse.batchSeal')} open={open} width={1000}
      onCancel={onClose} onOk={handleOk}>
      <Spin spinning={!!loading}>
        <Flex vertical gap={4} style={{ height: "60vh" }}>
          <STableLayout
            virtual
            dataSource={filterdItems}
            total={filterdItems.length}
            pagination={false}
            extHeight={100}
            headerChildren={headerChildren}
            columns={columns as AnyObject[]}
          />
        </Flex>
      </Spin>
      <ExportProgressModal processLabel={t('warehouse.batchSeal')} title={t('warehouse.batchSeal') + '...'} visible={loading} progress={sealProgress} />
    </Modal>
  )
};

export default BatchSeal;